/**
 * TripCard (docs/11 §3.3 state D): after "Head there" the app is a leave-by
 * clock and a curb until the driver says how it went. One primary (Open
 * Maps, 72 px, go-green), one quiet exit. Reads from the stored attempt so
 * it survives reloads and no signal.
 */
import { CarFront, Navigation, MapPin, ExternalLink } from 'lucide-react';
import type { StoredAttempt, MapsProvider } from './storage.js';
import { mapsUrl, MAPS_LABEL } from './attempt.js';
import { Button } from './ui.js';
import { Countdown } from './EventCard.js';
import { fmtWindow, fmtTime, headingShort } from './format.js';

export function TripCard({ attempt, now, maps, heading, busy, onHere, onDidntGo }: {
  attempt: StoredAttempt;
  now: number;
  maps: MapsProvider;
  heading: number | 'lt5' | null;
  busy: boolean;
  onHere: () => void;
  onDidntGo: () => void;
}) {
  const p10 = new Date(attempt.p10_at).getTime();
  const p90 = new Date(attempt.p90_at).getTime();
  const exiting = now >= p10 && now <= p90;
  const over = now > p90;
  const beforeLeave = attempt.leave_by && new Date(attempt.leave_by).getTime() > now;

  return (
    <section className="card trip" aria-label="Heading there">
      <span className="overline go"><CarFront size={16} aria-hidden="true" /> Heading there</span>

      {over ? (
        <>
          <span className="label">Crowd should be out</span>
          <div className="display">Done?</div>
        </>
      ) : exiting ? (
        <>
          <span className="label">Crowd is coming out</span>
          <div className="display" style={{ color: 'var(--go)' }}>Now</div>
          <span className="sub num">until about {fmtTime(attempt.p90_at)}</span>
        </>
      ) : beforeLeave ? (
        <>
          <span className="label">Leave in</span>
          <Countdown target={attempt.leave_by!} now={now} />
          <span className="sub num">crowd out {fmtWindow(attempt.p10_at, attempt.p90_at)}</span>
        </>
      ) : (
        <>
          <span className="label">Crowd out in</span>
          <Countdown target={attempt.p10_at} now={now} />
          <span className="sub num">{fmtWindow(attempt.p10_at, attempt.p90_at)}</span>
        </>
      )}

      <h2 className="title" style={{ marginTop: 6 }}>{attempt.title}</h2>
      <div className="inset">
        <MapPin size={18} aria-hidden="true" />
        <span>
          <b>{attempt.staging_label}</b>
          {attempt.staging_approach ? ` — ${attempt.staging_approach}` : ''}
        </span>
      </div>
      {heading !== null && (
        <div className="meta"><CarFront size={18} aria-hidden="true" /><span>{headingShort(heading)}</span></div>
      )}

      <div className="card-actions">
        <a className="btn go" href={mapsUrl(maps, attempt.staging_lat, attempt.staging_lng)}
          target="_blank" rel="noopener" aria-label={`Open ${MAPS_LABEL[maps]} to the pickup spot`}>
          <Navigation size={22} aria-hidden="true" /> Open Maps <ExternalLink size={16} aria-hidden="true" />
        </a>
        <div className="btn-row">
          <Button variant="secondary" busy={busy} onClick={onHere}>I’m here</Button>
          <Button variant="quiet" className="end" busy={busy} onClick={onDidntGo}>Didn’t go</Button>
        </div>
      </div>
    </section>
  );
}
